import { useQuery } from 'react-query';
import { useEffect } from 'react';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import useScrollToTop from 'src/hooks/useScrollToTop';
import SessionService from 'src/services/SessionService';
import useSessionStore from 'src/stores/session';

interface IAuthProviderProps {
    children: React.ReactNode;
}

export default function AuthProvider({ children }: IAuthProviderProps) {
    useScrollToTop();
    const hasToken = Boolean(SessionService.accessToken);

    const { getUserSessionRequiredData, isAuthorized, checkUserDataChanges } = useSessionStore(
        ({ isAuthorized, getUserSessionRequiredData, checkUserDataChanges }) => ({
            isAuthorized,
            getUserSessionRequiredData,
            checkUserDataChanges,
        })
    );

    const { isLoading: isUserLoading } = useQuery('user', getUserSessionRequiredData, {
        enabled: hasToken || isAuthorized,
        refetchOnMount: false, // Используем кеш, чтобы не делать повторные запросы
        staleTime: 30000, // Данные считаются свежими 30 секунд
        cacheTime: 60000, // Кеш хранится 60 секунд
    });

    // Умное обновление данных пользователя
    useEffect(() => {
        if (!isAuthorized) {
            console.log('🔒 Пользователь не авторизован, умное обновление отключено');
            return;
        }

        console.log('🚀 Запуск умного обновления данных пользователя');

        const interval = setInterval(async () => {
            // Проверяем только если вкладка активна
            if (document.hidden) {
                console.log('👁️ Вкладка неактивна, пропускаем проверку');
                return;
            }

            console.log('⏰ Проверяем изменения данных пользователя...');

            try {
                const hasChanges = await checkUserDataChanges();
                if (hasChanges) {
                    console.log('✅ Данные пользователя успешно обновлены');
                } else {
                    console.log('ℹ️ Изменений не обнаружено');
                }
            } catch (error) {
                console.error('❌ Ошибка при проверке изменений:', error);
            }
        }, 15000); // Проверяем каждые 15 секунд

        // Проверяем изменения при возвращении фокуса на вкладку
        const handleVisibilityChange = async () => {
            if (!document.hidden) {
                console.log('👁️ Вкладка стала активной, проверяем изменения...');
                try {
                    await checkUserDataChanges();
                } catch (error) {
                    console.error('❌ Ошибка при проверке изменений при фокусе:', error);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            console.log('🛑 Остановка умного обновления');
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isAuthorized, checkUserDataChanges]);

    return (
        <LoadingWrapper
            isLoading={isUserLoading}
            isForced={!isAuthorized && hasToken}
        >
            {children}
        </LoadingWrapper>
    );
}
